import { useState, useCallback, useRef, useEffect } from "react";

import {
  validateEmailFull,
  EmailValidationResult,
  EmailValidationStatus,
  EmailSuggestion,
  FullValidationOptions,
  checkEmailTypo,
  validateEmailSyntax,
} from "../utils/validators/emailValidation";

// ============================================================================
// Types
// ============================================================================

export interface UseEmailValidationOptions extends FullValidationOptions {
  /** Debounce delay in ms (default: 300) */
  debounceMs?: number;
  /** Callback when validation completes */
  onValidationComplete?: (result: EmailValidationResult) => void;
  /** Callback when suggestion is found */
  onSuggestion?: (suggestion: EmailSuggestion) => void;
}

export interface UseEmailValidationReturn {
  /** Current validation status */
  status: EmailValidationStatus;
  /** Whether validation is in progress */
  isValidating: boolean;
  /** Whether the email is valid */
  isValid: boolean;
  /** Error message if any */
  errorMessage: string | null;
  /** Typo suggestion if any */
  suggestion: EmailSuggestion | null;
  /** Whether the last validation had a network error (but was allowed through) */
  wasNetworkError: boolean;
  /** Validate the email (call on blur) */
  validate: (email: string) => Promise<EmailValidationResult>;
  /** Check for typos only (instant, call on change) */
  checkTypo: (email: string) => Promise<void>;
  /** Clear validation state */
  clear: () => void;
  /** Accept the suggestion */
  acceptSuggestion: () => string | null;
  /** Dismiss the suggestion */
  dismissSuggestion: () => void;
}

// ============================================================================
// Hook
// ============================================================================

export const useEmailValidation = (
  options: UseEmailValidationOptions = {}
): UseEmailValidationReturn => {
  const {
    debounceMs = 300,
    onValidationComplete,
    onSuggestion,
    ...validationOptions
  } = options;

  // State
  const [status, setStatus] = useState<EmailValidationStatus>("idle");
  const [isValidating, setIsValidating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<EmailSuggestion | null>(null);
  const [wasNetworkError, setWasNetworkError] = useState(false);

  // Refs for stable access (avoid stale closures in validate callback)
  const statusRef = useRef(status);
  const errorMessageRef = useRef(errorMessage);
  const suggestionRef = useRef(suggestion);
  const wasNetworkErrorRef = useRef(wasNetworkError);
  const validationOptionsRef = useRef(validationOptions);
  const onValidationCompleteRef = useRef(onValidationComplete);
  const onSuggestionRef = useRef(onSuggestion);

  // Keep refs in sync with state/props
  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { errorMessageRef.current = errorMessage; }, [errorMessage]);
  useEffect(() => { suggestionRef.current = suggestion; }, [suggestion]);
  useEffect(() => { wasNetworkErrorRef.current = wasNetworkError; }, [wasNetworkError]);
  useEffect(() => { validationOptionsRef.current = validationOptions; });
  useEffect(() => { onValidationCompleteRef.current = onValidationComplete; }, [onValidationComplete]);
  useEffect(() => { onSuggestionRef.current = onSuggestion; }, [onSuggestion]);

  // Refs for debouncing
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastValidatedEmailRef = useRef<string>("");
  const currentSuggestionRef = useRef<EmailSuggestion | null>(null);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  /**
   * Check for typos only (instant, no debounce)
   */
  const checkTypo = useCallback(async (email: string) => {
    if (!email || !validateEmailSyntax(email)) {
      setSuggestion(null);
      currentSuggestionRef.current = null;
      return;
    }

    const typoSuggestion = await checkEmailTypo(email);
    setSuggestion(typoSuggestion);
    currentSuggestionRef.current = typoSuggestion;

    if (typoSuggestion && onSuggestionRef.current) {
      onSuggestionRef.current(typoSuggestion);
    }
  }, []);

  /**
   * Full validation (debounced)
   */
  const validate = useCallback(
    async (email: string): Promise<EmailValidationResult> => {
      const trimmedEmail = email?.trim() || "";

      // Clear any pending debounce
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      // If empty and not required, return valid
      if (!trimmedEmail && !validationOptionsRef.current.required) {
        const result: EmailValidationResult = {
          isValid: true,
          status: "idle",
          errorType: null,
          errorMessage: null,
          suggestion: null,
          wasNetworkError: false,
        };
        setStatus("idle");
        setErrorMessage(null);
        setSuggestion(null);
        currentSuggestionRef.current = null;
        setWasNetworkError(false);
        return result;
      }

      // Skip if we already validated this email (read from refs to avoid stale closures)
      if (trimmedEmail === lastValidatedEmailRef.current && statusRef.current !== "idle") {
        return {
          isValid: statusRef.current === "valid" || statusRef.current === "unknown",
          status: statusRef.current,
          errorType: null,
          errorMessage: errorMessageRef.current,
          suggestion: suggestionRef.current,
          wasNetworkError: wasNetworkErrorRef.current,
        };
      }

      // Start validation
      setIsValidating(true);
      setStatus("pending");

      return new Promise((resolve) => {
        // Debounce the actual validation
        debounceTimerRef.current = setTimeout(async () => {
          try {
            const result = await validateEmailFull(trimmedEmail, validationOptionsRef.current);

            // Update state
            lastValidatedEmailRef.current = trimmedEmail;
            setStatus(result.status);
            setErrorMessage(result.errorMessage);
            setWasNetworkError(result.wasNetworkError);

            // Always update suggestion (clear stale ones when result has no suggestion)
            setSuggestion(result.suggestion);
            currentSuggestionRef.current = result.suggestion;
            if (result.suggestion && onSuggestionRef.current) {
              onSuggestionRef.current(result.suggestion);
            }

            // Callback
            if (onValidationCompleteRef.current) {
              onValidationCompleteRef.current(result);
            }

            resolve(result);
          } catch (error) {
            // Unexpected error - fail open
            console.error("Email validation unexpected error:", error);
            const result: EmailValidationResult = {
              isValid: true,
              status: "unknown",
              errorType: "network",
              errorMessage: null,
              suggestion: null,
              wasNetworkError: true,
            };
            setStatus("unknown");
            setSuggestion(null);
            currentSuggestionRef.current = null;
            setWasNetworkError(true);
            resolve(result);
          } finally {
            setIsValidating(false);
          }
        }, debounceMs);
      });
    },
    [debounceMs]
  );

  /**
   * Clear all validation state
   */
  const clear = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setStatus("idle");
    setIsValidating(false);
    setErrorMessage(null);
    setSuggestion(null);
    setWasNetworkError(false);
    lastValidatedEmailRef.current = "";
    currentSuggestionRef.current = null;
  }, []);

  /**
   * Accept the current suggestion
   * Returns the suggested email or null if no suggestion
   */
  const acceptSuggestion = useCallback((): string | null => {
    const currentSuggestion = currentSuggestionRef.current;
    if (currentSuggestion) {
      setSuggestion(null);
      currentSuggestionRef.current = null;
      // Clear validation state so it can be re-validated with new value
      lastValidatedEmailRef.current = "";
      return currentSuggestion.full;
    }
    return null;
  }, []);

  /**
   * Dismiss the current suggestion
   */
  const dismissSuggestion = useCallback(() => {
    setSuggestion(null);
    currentSuggestionRef.current = null;
  }, []);

  return {
    status,
    isValidating,
    isValid: status === "valid" || status === "unknown",
    errorMessage,
    suggestion,
    wasNetworkError,
    validate,
    checkTypo,
    clear,
    acceptSuggestion,
    dismissSuggestion,
  };
};

export default useEmailValidation;
